import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import UnifiedSidebar from "../components/UnifiedSidebar";
import Header from "../components/Header";
import CliniQueueLoading from "../components/CliniQueueLoading";
import { useSubscription } from "../context/SubscriptionContext";

const LOADER_SESSION_KEY = "clinique.initial-loader-completed";

function ClinicAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriptionData } = useSubscription();
  const [isTvMode, setIsTvMode] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showInitialLoader, setShowInitialLoader] = useState(() => {
    try {
      return sessionStorage.getItem(LOADER_SESSION_KEY) !== "true";
    } catch {
      return true;
    }
  });

  const mode = subscriptionData?.mode === "ACTIVE" ? "SUBSCRIBED" : subscriptionData?.mode;
  const isSubscriptionPage =
    location.pathname.replace(/\/+$/, "") === "/admin-dashboard/subscription";

  useEffect(() => {
    if (!showInitialLoader) return undefined;

    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(LOADER_SESSION_KEY, "true");
      } catch {
        // Continue even when browser storage is unavailable.
      }
      setShowInitialLoader(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, [showInitialLoader]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsTvMode(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (mode === "EXPIRED" && !isSubscriptionPage) {
    return (
      <CliniQueueLoading
        progress={100}
        animate={false}
        subscriptionMode="EXPIRED"
        onRenew={() => navigate("/admin-dashboard/subscription")}
      />
    );
  }

  if (showInitialLoader && !isSubscriptionPage) {
    return (
      <CliniQueueLoading
        progress={0}
        animate={true}
        subscriptionMode={mode}
        onRenew={() => navigate("/admin-dashboard/subscription")}
      />
    );
  }

  const CSS = `
    html, body, #root { height: 100%; margin: 0; }

    .admin-layout-wrapper {
      display: flex;
      min-height: 100vh;
      height: 100vh;
      overflow: hidden;
      background: linear-gradient(180deg, #ecf8ff 0%, #ffffff 100%);
      font-family: 'Outfit', sans-serif;
      /* ensure layout fills viewport so internal panels control scrolling */
    }

    .admin-main {
      margin-left: 270px;
      width: calc(100% - 270px);
      display: flex;
      flex-direction: column;
      height: 100vh;
      min-height: 0;
      position: relative;
    }

    .admin-content {
      flex: 1;
      /* constrain scrolling to this element only when content overflows */
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      padding: 32px;
      background: linear-gradient(180deg, rgba(236,248,255,0.6) 0%, #ffffff 100%);
    }

    @media (max-width: 1200px) {
      .admin-layout-wrapper {
        flex-direction: column;
      }

      .admin-main {
        margin-left: 0;
        width: 100%;
      }

      .admin-content {
        padding: 24px 20px;
      }
    }

    @media (max-width: 768px) {
      .admin-content {
        padding: 20px 16px;
      }
    }
  `;

  return (
    <>
      <style>{CSS}</style>
      <div className="admin-layout-wrapper">
        {!isTvMode && (
          <UnifiedSidebar
            role="admin"
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
          />
        )}
        <div className="admin-main">
          {!isTvMode && <Header onMenuClick={() => setMobileOpen(true)} />}
          <div className="admin-content">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
}

export default ClinicAdminLayout;